import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Brain, ShieldCheck, ArrowRight, X, MessageSquare, Search, Code2, Layers } from "lucide-react";
import { toast } from "sonner";

export default function Landing() {
  const { user, login, register } = useAuth();
  const navigate = useNavigate();
  const [authOpen, setAuthOpen] = useState(false);
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const openAuth = (m = "login") => {
    if (user) return navigate("/app");
    setMode(m); setAuthOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") await login(email, password);
      else await register(email, password, name);
      setAuthOpen(false);
      toast.success("Welcome to AfterMind");
      navigate("/app");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Authentication failed");
    } finally { setBusy(false); }
  };

  const capabilities = [
    { icon: MessageSquare, title: "Chat & Reason", desc: "Deep, multi-turn conversation with memory." },
    { icon: Search, title: "Real-time Research", desc: "Web-aware answers with sources." },
    { icon: Code2, title: "Code & Math", desc: "Write, debug, explain across every stack." },
    { icon: Layers, title: "Projects & Personas", desc: "Doctor / Lawyer / Coder / Marketer modes." },
  ];

  return (
    <div className="relative min-h-screen overflow-x-hidden" data-testid="landing-page">
      <div className="aura-bloom" />

      <nav className="relative z-10 flex items-center justify-between px-6 lg:px-12 py-5">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-500 via-fuchsia-500 to-cyan-400 flex items-center justify-center shadow-lg shadow-violet-500/40">
            <Brain className="w-5 h-5 text-white" strokeWidth={2.5} />
          </div>
          <div className="leading-tight">
            <div className="text-lg font-black tracking-tight aura-gradient-text">AfterMind AI</div>
            <div className="text-[10px] uppercase tracking-[0.24em] text-slate-500">Created by Lakshya Soni</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="nav-signin-btn" onClick={() => openAuth("login")} className="aura-btn-ghost px-4 py-2 rounded-full text-sm font-medium">Sign in</button>
          <button data-testid="nav-start-btn" onClick={() => openAuth("register")} className="aura-btn-primary px-5 py-2 rounded-full text-sm font-semibold text-white">Start Free</button>
        </div>
      </nav>

      <section className="relative z-10 px-6 lg:px-12 pt-8 lg:pt-16 pb-24 max-w-7xl mx-auto">
        <div className="flex flex-col items-start gap-6 slide-up">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full glass text-xs uppercase tracking-[0.22em] text-violet-300 font-semibold">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shadow-[0_0_8px_#10B981]" /> Live · One brain for everything AI
          </span>

          <h1 className="text-5xl sm:text-7xl lg:text-8xl font-black tracking-tighter leading-[0.9]">
            <span className="block text-slate-100">Hi, I'm</span>
            <span className="block aura-gradient-text">AfterMind.</span>
          </h1>

          <p className="text-sm uppercase tracking-[0.32em] text-slate-500 font-semibold">Created by Lakshya Soni · Building the future of personal intelligence</p>

          <p className="max-w-2xl text-xl sm:text-2xl text-slate-300 font-light leading-relaxed">
            <span className="text-slate-100 font-semibold">One Mind. Everything AI.</span> Chat, research, create images and video, code, speak,
            see, and run multi-step agents — all inside one unified workspace. Never leave AfterMind again.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 mt-4">
            <button data-testid="start-with-aura-btn" onClick={() => openAuth("register")} className="aura-btn-primary group px-8 py-4 rounded-full text-white font-semibold text-lg flex items-center gap-2">
              Tell me what you want to create
              <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
            </button>
            <button data-testid="landing-signin-btn" onClick={() => openAuth("login")} className="aura-btn-ghost px-8 py-4 rounded-full text-slate-200 font-medium text-lg">
              I already have an account
            </button>
          </div>
        </div>

        {/* One prompt → complete result */}
        <div className="mt-20 glass-strong rounded-3xl p-8 lg:p-12">
          <div className="text-xs uppercase tracking-[0.28em] text-cyan-300 font-semibold mb-3">One prompt · complete result</div>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-100 mb-6">Type it. AfterMind builds it.</h2>
          <div className="grid md:grid-cols-3 gap-3 text-sm">
            {[
              { p: "Create a 30-second premium ad for my brand", steps: "Script → visuals → voiceover → music → cut" },
              { p: "Make an Instagram post about our launch", steps: "Copy + on-brand image" },
              { p: "Build a presentation about AI in medicine", steps: "Research → outline → slides" },
              { p: "Analyze this PDF and explain it", steps: "Read → summarize → answer questions" },
              { p: "Debug this React component", steps: "Read code → find bug → propose patch" },
              { p: "Translate & narrate this in Hindi", steps: "Translate → generate voice" },
            ].map((s, i) => (
              <div key={i} className="glass rounded-xl p-4">
                <div className="text-slate-100 font-medium">“{s.p}”</div>
                <div className="text-xs text-slate-400 mt-1.5">{s.steps}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Capabilities */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-16">
          {capabilities.map((f, i) => (
            <div key={i} className="glass rounded-2xl p-5 hover:border-violet-500/40 transition-all">
              <f.icon className="w-6 h-6 text-violet-300 mb-3" />
              <div className="font-semibold text-slate-100 mb-1">{f.title}</div>
              <div className="text-xs text-slate-400 leading-relaxed">{f.desc}</div>
            </div>
          ))}
        </div>

        {/* Domains */}
        <div className="mt-16 glass-strong rounded-3xl p-8 lg:p-12">
          <div className="text-xs uppercase tracking-[0.28em] text-cyan-300 font-semibold mb-3">Every field · one assistant</div>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-100 mb-6">Built for real work</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 text-sm">
            {["Physics","Chemistry","Mathematics","Biology","Computer Science","DSA & Coding","Forensic Science","Engineering","Medical Research","Legal Drafting","Business","Marketing","Content Writing","Data Analysis","Career Growth","Research"].map((d) => (
              <div key={d} className="px-3 py-2 rounded-lg bg-white/5 border border-white/5 text-slate-300 hover:border-violet-500/40 transition">{d}</div>
            ))}
          </div>
        </div>

        {/* Plans */}
        <div className="mt-16 grid md:grid-cols-1 gap-4">
          <div className="glass-strong rounded-2xl p-7 relative border-violet-500/40">
            <div className="absolute -top-3 left-7 px-3 py-1 rounded-full text-[10px] tracking-[0.22em] font-semibold bg-gradient-to-r from-violet-500 to-cyan-400 text-slate-950">FREE FOREVER</div>
            <div className="text-xs uppercase tracking-[0.22em] text-violet-300 font-semibold mb-2">Unlimited chat</div>
            <div className="text-4xl font-black aura-gradient-text">₹0<span className="text-base font-normal text-slate-500">/month</span></div>
            <ul className="mt-5 grid sm:grid-cols-2 gap-y-2 text-sm text-slate-200">
              <li>• Unlimited chat & memory</li>
              <li>• Real-time web research</li>
              <li>• Doctor / Lawyer / Coder / Marketer modes</li>
              <li>• Code, math, writing & translation</li>
              <li>• File & PDF understanding</li>
              <li>• Conversation history & search</li>
            </ul>
          </div>
        </div>

        <div className="mt-16 flex items-center gap-3 text-xs text-slate-500">
          <ShieldCheck className="w-4 h-4 text-emerald-400" />
          Server-side quota · Encrypted memory · User data isolation · API keys never exposed
        </div>
      </section>

      {authOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 fade-in" onClick={() => setAuthOpen(false)}>
          <div className="absolute inset-0 bg-black/70 backdrop-blur-md" />
          <div className="relative glass-strong rounded-3xl p-7 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="auth-modal">
            <button data-testid="auth-close-btn" onClick={() => setAuthOpen(false)} className="absolute top-4 right-4 text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
            <div className="text-xs uppercase tracking-[0.24em] text-violet-300 font-semibold mb-2">{mode === "login" ? "Welcome back" : "Join AfterMind"}</div>
            <h3 className="text-2xl font-bold text-slate-100 mb-6">{mode === "login" ? "Sign in to continue" : "Create your account"}</h3>
            <form onSubmit={submit} className="space-y-3">
              {mode === "register" && (
                <input data-testid="auth-name-input" required value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" className="w-full bg-white/5 border border-white/10 focus:border-violet-400 rounded-xl px-4 py-3 text-slate-100 outline-none placeholder:text-slate-500" />
              )}
              <input data-testid="auth-email-input" required type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@aftermind.ai" className="w-full bg-white/5 border border-white/10 focus:border-violet-400 rounded-xl px-4 py-3 text-slate-100 outline-none placeholder:text-slate-500" />
              <input data-testid="auth-password-input" required type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password (min 6 chars)" className="w-full bg-white/5 border border-white/10 focus:border-violet-400 rounded-xl px-4 py-3 text-slate-100 outline-none placeholder:text-slate-500" />
              <button data-testid="auth-submit-btn" disabled={busy} type="submit" className="aura-btn-primary w-full py-3.5 rounded-xl text-white font-semibold">
                {busy ? "Please wait…" : (mode === "login" ? "Sign in" : "Create account")}
              </button>
            </form>
            <div className="mt-4 text-center text-sm text-slate-400">
              {mode === "login" ? (
                <>New here? <button data-testid="auth-switch-register" onClick={() => setMode("register")} className="text-violet-300 font-medium">Create account</button></>
              ) : (
                <>Have an account? <button data-testid="auth-switch-login" onClick={() => setMode("login")} className="text-violet-300 font-medium">Sign in</button></>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
